import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import {useAccess} from "../../utils/useAccess";
import BooleanInput from "../../elements/booleanInput.element";
import Collapsible from "../../elements/collapsible";
import LivePreviewDebugInfo from "../../elements/livePreviewDebugInfo";
import {AccessLevel} from "../../models/accessLevel.model";

function SettingsEditorView() {
    const {state, branch, getBranchVersionNumber} = useIntegrator();
    const {showIds, setShowIds, accessLevel, displayName, playFabId, logout, setViewJson, exportRemoteConstants, setLoading, confirm} = useApp();
    const {canExportLive, canPreview} = useAccess();

    // Download the current branch's RemoteConstants as JSON. Uses a Blob URL (not a
    // data: URI) so large branches don't hit URL-length limits.
    function downloadJson() {
        const blob = new Blob([JSON.stringify(state)], {type: 'application/json'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'remoteConstants.json';
        a.click();
        URL.revokeObjectURL(url);
    }

    // Publish the current branch to the live service (Admin+). Confirmed because it
    // impacts live players immediately.
    async function exportLive() {
        const proceed = await confirm({
            title: 'Export to LIVE SERVICE',
            body: <p>{`Export ${branch} v${getBranchVersionNumber(branch)} to the LIVE SERVICE? This immediately impacts live players.`}</p>,
            confirmText: 'Export to LIVE SERVICE',
            danger: true,
        });
        if (!proceed) return;
        setLoading(true);
        await exportRemoteConstants(true);
        setLoading(false);
    }

    return (
        <div className={'layout horizontal box flex-grow stretch'} style={{gap: 10}}>
            <div className={'layout vertical gap'} style={{width: 500, gap: 20}}>
                <p className={'text header'}>Editor Settings</p>
                <div className={'layout vertical gap box'} style={{padding: 16, gap: 12}}>
                    <div className={'layout horizontal center gap'}>
                        <BooleanInput value={showIds} onChange={setShowIds}/>
                        <div className={'layout vertical'}>
                            <p>Show IDs</p>
                            <p style={{color: 'var(--muted-text-color)', fontSize: 13}}>
                                Display the underlying id on attribute rows. Useful when writing code that references attributes; not usually needed for content design.
                            </p>
                        </div>
                    </div>
                </div>
                <div className={'layout vertical gap box'} style={{padding: 16, gap: 12}}>
                    <div className={'layout vertical'}>
                        <p>Data</p>
                        <p style={{color: 'var(--muted-text-color)', fontSize: 13}}>
                            The current branch's RemoteConstants JSON.
                        </p>
                    </div>
                    <div className={'layout horizontal gap'}>
                        <button className={'flex-grow'} onClick={downloadJson}>Download</button>
                        <button className={'flex-grow'} onClick={() => setViewJson(true)}>View JSON</button>
                    </div>
                </div>
                {canPreview &&
                    <div className={'layout vertical gap box'} style={{padding: 16, gap: 12}}>
                        <p>Debug Info</p>
                        <LivePreviewDebugInfo/>
                    </div>}
                <div className={'layout vertical gap box'} style={{padding: 16, gap: 12}}>
                    <div className={'layout horizontal center stretch gap'}>
                        <div className={'layout vertical'}>
                            <p>Account</p>
                            <p style={{color: 'var(--muted-text-color)', fontSize: 13}}>
                                Signed in as <strong>{displayName || playFabId || 'unknown'}</strong> &mdash; access level: {AccessLevel[accessLevel]}.
                            </p>
                        </div>
                        <button onClick={logout}>Sign Out</button>
                    </div>
                </div>
                {/* Collapsed by default (uncontrolled — state isn't persisted). */}
                {canExportLive &&
                    <div className={'layout vertical gap box'} style={{padding: 16, gap: 12}}>
                        <Collapsible title={<p>Publish</p>} defaultOpen={false}>
                            <div className={'layout vertical gap'} style={{gap: 12, marginTop: 12}}>
                                <p style={{color: 'var(--muted-text-color)', fontSize: 13}}>
                                    Export the current branch ({branch} v{getBranchVersionNumber(branch)}) to the live service. This immediately impacts live players.
                                </p>
                                <button className={'export-button production'} onClick={exportLive}>Export to LIVE SERVICE</button>
                            </div>
                        </Collapsible>
                    </div>}
            </div>
        </div>
    )
}

export default SettingsEditorView;
